import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTenant } from '../tenancy/TenantContext';
import {
  getTenantPublishStatus,
  getTenantSiteDraft,
  publishTenantSite,
  updateTenantSiteDraft,
  validateTenantPublish,
  getMyTenantOnboardingStatus,
  confirmMyTenantSettings,
  setMyTenantLocalSubscription,
  activateMyTenant,
  saveMyOnboardingMode,
  confirmMyOnboardingPlan,
  startMyOnboardingStep,
  saveMyOnboardingAvailability,
  chooseMyOnboardingSiteExperience,
  completeMyOnboardingIntroduction,
  updateMyOnboardingBusinessDetails,
  completeMyOnboardingService,
  completeMyOnboardingSiteIntroduction,
  adoptMyPreparedServices,
} from './api';
import { sitePublishingKeys } from './queryKeys';
import {
  OnboardingStepId,
  SaveMyOnboardingAvailabilityInput,
  SaveMyOnboardingModeInput,
  ConfirmMyOnboardingPlanInput,
  SiteDraft,
  SiteExperience,
  TenantPublishingError,
} from './types';

const shouldRetryQuery = (failureCount: number, error: Error): boolean => {
  if (failureCount >= 2) return false;
  if (error instanceof TenantPublishingError && [
    'UNAUTHENTICATED', 'FORBIDDEN', 'INVALID_ARGUMENT', 'NOT_FOUND',
    'DRAFT_VERSION_CONFLICT', 'IDEMPOTENCY_CONFLICT', 'PUBLISH_VALIDATION_FAILED',
  ].includes(error.code)) return false;
  return true;
};

const shouldRetryIdempotentMutation = (failureCount: number, error: Error): boolean => {
  if (failureCount >= 1) return false;
  if (error instanceof TenantPublishingError && error.code !== 'UNKNOWN' && error.code !== 'INTERNAL') {
    return false;
  }
  return true;
};

export const useTenantPublishStatus = (enabled = true) => {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: sitePublishingKeys.status(tenantId),
    queryFn: () => getTenantPublishStatus(tenantId),
    retry: shouldRetryQuery,
    staleTime: 60_000,
    enabled,
  });
};

export const useTenantOnboardingStatus = (enabled = true) => {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: sitePublishingKeys.onboarding(tenantId),
    queryFn: () => getMyTenantOnboardingStatus(tenantId),
    enabled,
    retry: shouldRetryQuery,
    staleTime: 5_000,
    refetchOnWindowFocus: true,
  });
};

export const useConfirmTenantSettings = () => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => confirmMyTenantSettings(tenantId),
    retry: false,
    onSuccess: (status) => {
      queryClient.setQueryData(sitePublishingKeys.onboarding(tenantId), status);
    },
  });
};

export const useLocalTenantSubscription = () => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => setMyTenantLocalSubscription(tenantId),
    retry: false,
    onSuccess: (status) => {
      if ('steps' in status) queryClient.setQueryData(sitePublishingKeys.onboarding(tenantId), status);
      queryClient.invalidateQueries({ queryKey: ['businessConfig', tenantId], exact: true });
      queryClient.invalidateQueries({ queryKey: sitePublishingKeys.draft(tenantId), exact: true });
      queryClient.invalidateQueries({ queryKey: sitePublishingKeys.status(tenantId), exact: true });
    },
  });
};

export const useActivateMyTenant = () => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => activateMyTenant(tenantId),
    retry: false,
    onSuccess: () => {
      // Billing is checked against business settings once the site is active,
      // so reload them instead of trusting a copy loaded before setup.
      queryClient.invalidateQueries({ queryKey: ['businessConfig', tenantId], exact: true });
      queryClient.invalidateQueries({ queryKey: sitePublishingKeys.onboarding(tenantId), exact: true });
      queryClient.invalidateQueries({ queryKey: sitePublishingKeys.status(tenantId), exact: true });
    },
  });
};

const useOnboardingStatusMutation = <TInput>(mutation: (tenantId: string, input: TInput) =>
Promise<import('./types').TenantOnboardingStatus>) => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => mutation(tenantId, input),
    retry: false,
    onSuccess: (status) => {
      queryClient.setQueryData(sitePublishingKeys.onboarding(tenantId), status);
      queryClient.invalidateQueries({ queryKey: ['businessConfig', tenantId], exact: true });
    },
  });
};

export const useSaveOnboardingMode = () =>
  useOnboardingStatusMutation<SaveMyOnboardingModeInput>(saveMyOnboardingMode);

export const useConfirmOnboardingPlan = () =>
  useOnboardingStatusMutation<ConfirmMyOnboardingPlanInput>(confirmMyOnboardingPlan);

export const useStartOnboardingStep = () =>
  useOnboardingStatusMutation<OnboardingStepId>(startMyOnboardingStep);

export const useSaveOnboardingAvailability = () =>
  useOnboardingStatusMutation<SaveMyOnboardingAvailabilityInput>(saveMyOnboardingAvailability);

export const useChooseOnboardingSiteExperience = () =>
  useOnboardingStatusMutation<SiteExperience>(chooseMyOnboardingSiteExperience);

export const useCompleteOnboardingIntroduction = () => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => completeMyOnboardingIntroduction(tenantId),
    retry: false,
    onSuccess: (status) => queryClient.setQueryData(sitePublishingKeys.onboarding(tenantId), status),
  });
};

export const useUpdateOnboardingBusinessDetails = () =>
  useOnboardingStatusMutation<{ displayName: string; timezone: string; businessType?: string }>(
    updateMyOnboardingBusinessDetails
  );

export const useAdoptPreparedServices = () =>
  useOnboardingStatusMutation<void>((tenantId) => adoptMyPreparedServices(tenantId));

export const useCompleteOnboardingService = () =>
  useOnboardingStatusMutation<{ serviceId: string; category?: string }>(completeMyOnboardingService);

export const useCompleteOnboardingSiteIntroduction = () => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => completeMyOnboardingSiteIntroduction(tenantId),
    retry: false,
    onSuccess: (status) => queryClient.setQueryData(sitePublishingKeys.onboarding(tenantId), status),
  });
};

export const useTenantSiteDraft = () => {
  const { tenantId } = useTenant();
  return useQuery({
    queryKey: sitePublishingKeys.draft(tenantId),
    queryFn: () => getTenantSiteDraft(tenantId),
    retry: shouldRetryQuery,
    staleTime: 30_000,
  });
};

export const useUpdateTenantSiteDraft = () => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { expectedDraftVersion: number; schemaVersion: number; content: SiteDraft }) =>
      updateTenantSiteDraft(tenantId, input),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sitePublishingKeys.draft(tenantId), exact: true });
      queryClient.invalidateQueries({ queryKey: sitePublishingKeys.status(tenantId), exact: true });
      queryClient.invalidateQueries({ queryKey: sitePublishingKeys.onboarding(tenantId), exact: true });
    },
  });
};

export const useValidateTenantPublish = () => {
  const { tenantId } = useTenant();
  return useMutation({
    mutationFn: (expectedDraftVersion: number) => validateTenantPublish(tenantId, expectedDraftVersion),
    retry: false,
  });
};

export const usePublishTenantSite = () => {
  const { tenantId } = useTenant();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { expectedDraftVersion: number; idempotencyKey: string; confirmOnboardingSetup?: boolean }) =>
      publishTenantSite(tenantId, input),
    retry: shouldRetryIdempotentMutation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sitePublishingKeys.status(tenantId), exact: true });
      queryClient.invalidateQueries({ queryKey: sitePublishingKeys.onboarding(tenantId), exact: true });
      queryClient.invalidateQueries({ queryKey: ['homepageRuntimeConfig', tenantId], exact: true });
    },
  });
};
